// Hydrates <oc-giveaway-thumb> elements in the cart with the image of the
// giveaway attached to the line item (properties[_giveaway_id], written by
// oc-giveaway-picker.js on the product page).
//
// The image URL is not stored in Shopify — it lives in the OC API and is
// resolved client-side through window.OCGiveaways (assets/oc-giveaways.js).
// The element renders `hidden` from Liquid and only reveals itself once the
// giveaway is found in the cache, so an ended or unknown giveaway degrades to
// the text-only row rather than a broken thumbnail.
//
// Cart updates re-render the drawer through the Section Rendering API and
// morph.js reconciles the DOM against that server HTML. Two consequences
// shape this file:
//
//   1. The image is painted as an inline background-image on the media span,
//      NOT an injected <img>. Server HTML has no <img>, and morph reconciles
//      children even under data-skip-node-update, so an injected child is
//      stripped on every quantity change. The media span carries
//      data-skip-node-update (in both server and client HTML), which protects
//      its attributes — including this inline style — while leaving it
//      childless and therefore always equal to the server's child list.
//
//   2. The morph re-applies the server's `hidden` to an element this script
//      already revealed. `hidden` is observed, and the re-render is deferred
//      to a microtask so it runs after the whole morph pass, not from inside
//      morph's attribute loop.
//
// oc-giveaways.js is injected on demand (from data-api-src) instead of being a
// <script> tag on every page: carts without a giveaway line never hit the OC
// API, and pages where the picker block already loaded it reuse that instance.
(function () {
  if (customElements.get("oc-giveaway-thumb")) return;

  let apiLoader = null;

  function ensureGiveawaysApi(src) {
    if (window.OCGiveaways) return Promise.resolve();
    if (!src) return Promise.reject(new Error("missing data-api-src"));
    if (!apiLoader) {
      apiLoader = new Promise(function (resolve, reject) {
        const script = document.createElement("script");
        script.src = src;
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
      });
    }
    return apiLoader;
  }

  class OCGiveawayThumb extends HTMLElement {
    static get observedAttributes() {
      return ["giveaway-id", "hidden"];
    }

    connectedCallback() {
      this._onGiveawaysUpdated = this.render.bind(this);
      document.addEventListener("oc:giveaways:updated", this._onGiveawaysUpdated);

      const element = this;
      ensureGiveawaysApi(this.dataset.apiSrc)
        .then(function () {
          // The 5-minute TTL matters here: a drawer can open long after the
          // pageload that filled the cache. refresh() dedupes in-flight
          // fetches, and a change repaints via oc:giveaways:updated.
          if (window.OCGiveaways.isStale()) window.OCGiveaways.refresh();
          element.render();
        })
        .catch(function () {
          // API script unreachable — the element simply stays hidden.
        });
    }

    disconnectedCallback() {
      document.removeEventListener("oc:giveaways:updated", this._onGiveawaysUpdated);
    }

    attributeChangedCallback() {
      // Defer past the current task: morph applies attributes and reconciles
      // children in one pass, and rendering mid-pass leaves the element in
      // whatever state the rest of the pass imposes.
      if (this._renderQueued) return;
      this._renderQueued = true;
      const element = this;
      queueMicrotask(function () {
        element._renderQueued = false;
        if (element.isConnected) element.render();
      });
    }

    render() {
      if (!window.OCGiveaways) return;

      const giveaway = window.OCGiveaways.getById(this.getAttribute("giveaway-id"));
      const media = this.querySelector("[data-oc-giveaway-media]");

      if (!giveaway || !giveaway.imgUrl || !media) {
        if (!this.hidden) this.hidden = true;
        return;
      }

      const imageValue = 'url("' + encodeURI(giveaway.imgUrl) + '")';
      if (media.style.backgroundImage !== imageValue) {
        media.style.backgroundImage = imageValue;
      }
      if (this.hidden) this.hidden = false;
    }
  }

  customElements.define("oc-giveaway-thumb", OCGiveawayThumb);
})();
